import React, { FC, ReactElement, useMemo } from 'react';
import { useSelector } from 'react-redux';
import cns from 'classnames';
import chroma from 'chroma-js';
import { isSectionVisible, getSectionBackground, DataDto, getSectionClassNames, internalizationState } from '../stores/reducers/sections';
import Intro from './Pages/HomePage/Intro/Intro';
import Counter from './Pages/HomePage/Counter/Counter';
import About from './Pages/HomePage/About/About';
import Schedule from './Pages/HomePage/Schedule/Schedule';
import Program from './Pages/HomePage/Program/Program';
import Speakers from './Pages/HomePage/Speakers/Speakers';
import News from './Pages/HomePage/News/News';
import Partners from './Pages/HomePage/Partners/Partners';
import Prefooter from './Pages/HomePage/Prefooter/Prefooter';
import RegisterPage from './Pages/RegisterPage';
import LivePage from './Pages/LivePage/LivePage';
import StaticPage from './Pages/StaticPage/StaticPage';
import SpeakersPage from './Pages/SpeakersPage/SpeakersPage';
import localeFieldResolver from '../helpers/locale-field-finder';
import TextBlocks from './Pages/HomePage/TextBlocks/TextBlocks';
import MiniLanding from './Pages/HomePage/MiniLanding/MiniLanding';

interface Props {
  className?: string | Array<string>;
  data: DataDto;
  newsId?: string;
}

const Section: FC<Props> = ({ className, children, data, newsId }): ReactElement => {
  const isVisible = useSelector(isSectionVisible(data.id));
  const sectionBgColor = useSelector(getSectionBackground(data.id));
  const sectionClassNames = useSelector(getSectionClassNames(data.id));
  const langConfig = useSelector(internalizationState);

  // compare brightnerss to font color to see if it's non-readable
  const isDarkBackground = useMemo(() => {
    if (sectionBgColor) {
      const contrastAgainstFontColor = chroma.contrast(sectionBgColor, '#1F2041');

      // https://www.w3.org/TR/WCAG20-TECHS/G18.html
      return contrastAgainstFontColor < 5;
    }
    return false;
  }, [sectionBgColor]);

  const bgProps = {
    bgSettings: {
      color: sectionBgColor,
      isDark: isDarkBackground,
    },
  };

  const getComponent = useMemo(() => {
    const dataLocale = {title: data.name as string, title_en: data.name_en as string};
    
    switch (data.component) {
      case 'Intro':
        return <Intro sectionId={data.id.toString()} sectionData={data.params} />;
      case 'Counter':
        return <Counter {...bgProps} />;
      case 'About':
        return (
          <About
            className={sectionClassNames}
            {...bgProps}
            sectionName={localeFieldResolver(dataLocale, 'title', langConfig.activeLangState)}
            sectionId={data.id.toString()}
          />
        );
      case 'Schedule':
        return (
          <Schedule
            {...bgProps}
            sectionName={localeFieldResolver(dataLocale, 'title', langConfig.activeLangState)}
            sectionId={data.id.toString()}
            sectionData={data.params}
          />
        );
      case 'Program':
        return (
          <Program
            className={sectionClassNames}
            {...bgProps}
            sectionName={localeFieldResolver(dataLocale, 'title', langConfig.activeLangState)}
            sectionId={data.id.toString()}
            sectionData={data.params}
          />
        );
      case 'Speakers':
        return (
          <Speakers
            {...bgProps}
            sectionName={localeFieldResolver(dataLocale, 'title', langConfig.activeLangState)}
            sectionId={data.id.toString()}
            sectionData={data.params}
          />
        );
      case 'News':
        return (
          <News
            {...bgProps}
            sectionName={localeFieldResolver(dataLocale, 'title', langConfig.activeLangState)}
            sectionId={data.id.toString()}
          />
        );
      case 'Partners':
        return (
          <Partners
            {...bgProps}
            sectionName={localeFieldResolver(dataLocale, 'title', langConfig.activeLangState)}
            sectionId={data.id.toString()}
          />
        );
      case 'Prefooter':
        return (
          <Prefooter
            {...bgProps}
            sectionName={localeFieldResolver(dataLocale, 'title', langConfig.activeLangState)}
            sectionId={data.id.toString()}
          />
        );
      case 'RegisterPage':
        return (
          <RegisterPage
            {...bgProps}
            sectionName={localeFieldResolver(dataLocale, 'title', langConfig.activeLangState)}
            sectionId={data.id.toString()}
          />
        );
      case 'Live':
        return <LivePage {...bgProps} sectionId={data.id.toString()} />;
      case 'StaticPage':
        return <StaticPage {...bgProps} newsId={newsId} />;
      case 'SpeakersPage':
        return (
          <SpeakersPage
            {...bgProps}
            sectionName={localeFieldResolver(dataLocale, 'title', langConfig.activeLangState)}
          />
        );
      case 'TextBlocks':
        return (
          <TextBlocks
            {...bgProps}
            sectionName={localeFieldResolver(dataLocale, 'title', langConfig.activeLangState)}
            sectionData={data}
            sectionId={data.id.toString()}
          />
        );
      case 'MiniLanding':
        return (
          <MiniLanding
            {...bgProps}
            sectionName={localeFieldResolver(dataLocale, 'title', langConfig.activeLangState)}
            sectionData={data}
            sectionId={data.id.toString()}
          />
        );
      default:
        return null;
    }
  }, [data, bgProps, newsId, sectionClassNames, langConfig.activeLangState]);

  return (
    <>
      {isVisible && (
        <>
          {data.component ? (
            getComponent
          ) : (
            <div style={{ backgroundColor: sectionBgColor }}>
              <div className="container">
                <div className={cns(className, className)}>
                  <h1>{data.name}</h1>
                </div>
                <div className="params">
                  {data.params &&
                    data.params.map((p) => (
                      <p key={p.id} className="user-fs-text">
                        Param: {p.type} {p.value}
                      </p>
                    ))}
                </div>
                <div className="contents">{children}</div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default Section;
